<script setup lang="ts">
import { computed, watch } from "vue";
import { useOnline } from "@vueuse/core";
import { useQueryCache } from "@pinia/colada";

import { useApplicationStore } from "@/stores/Application";
import { useMarketClock } from "@/composables/useMarketClock";
import { formatSyncedAgo, isSyncStale } from "@/utils/sync-health";

// UX roadmap #58: offline, or while syncs kept failing, the app showed cached alerts and closes as if they were current,
// and after reconnecting the member waited for the next five-minute pass.
const appStore = useApplicationStore();
const queryCache = useQueryCache();
const online = useOnline();
// The shared 30 s clock: "N min ago" needs nothing finer.
const { now } = useMarketClock();

const updatedAgo = computed< string >( () => {
    const at = appStore.lastContentSyncAt;
    return null == at ? '' : formatSyncedAgo( at, now.value.getTime() );
} );
const isStale = computed< boolean >( () => online.value && isSyncStale( appStore.syncHealth, now.value.getTime() ) );

function resync(): void {
    appStore.resyncNow();
    // Colada queries (the watchlist) failed while offline and would otherwise wait out their staleTime.
    queryCache.invalidateQueries().catch( () => {
        /* A refetch that fails again leaves the query in its error state; the next reconnect retries. */
    } );
}

watch( online, ( isOnline, wasOnline ) => {
    if ( isOnline && !wasOnline ) {
        resync();
    }
} );
</script>

<template>
    <!-- One live region, always present, so the change of state is announced. It takes no space while empty. -->
    <div role="status" aria-live="polite" class="print:hidden">
        <p
            v-if="!online"
            data-testid="connection-banner"
            class="flex items-center justify-center gap-2 border-b border-warning/40 bg-warning/15 px-3 py-1.5 text-center text-sm"
        >
            <span class="pi pi-wifi text-warning" aria-hidden="true"></span>
            <span>You're offline. Showing content saved on this device<template v-if="updatedAgo"> (updated {{ updatedAgo }})</template>.</span>
        </p>
        <div
            v-else-if="isStale"
            data-testid="connection-banner"
            class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-warning/40 bg-warning/15 px-3 py-1.5 text-center text-sm"
        >
            <span>Some content may be out of date<template v-if="updatedAgo">: last updated {{ updatedAgo }}</template>.</span>
            <button type="button" class="btn btn-xs btn-ghost" @click="resync">Retry now</button>
        </div>
    </div>
</template>
